import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, client } from '@xinfer/api-client';
import { Overview } from './pages/Overview';
import { Keys } from './pages/Keys';
import { Usage } from './pages/Usage';
import { Limits } from './pages/Limits';
import { Quickstart } from './pages/Quickstart';

const PAGES = ['Overview', 'Keys', 'Usage', 'Limits', 'Quickstart'] as const;
type Page = (typeof PAGES)[number];

export function App() {
  const [page, setPage] = useState<Page>('Overview');
  const queryClient = useQueryClient();

  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => client.me(),
    // 401 is not a failure to retry: it means "show the login page".
    retry: (count, error) => !(error instanceof ApiError && error.isUnauthenticated) && count < 2,
  });

  const logout = useMutation({
    mutationFn: () => client.logout(),
    onSuccess: () => queryClient.clear(),
  });

  if (me.isLoading) {
    return <p className="muted" style={{ padding: 32 }}>Loading…</p>;
  }

  if (me.error instanceof ApiError && me.error.isUnauthenticated) {
    return <SignIn onSignedIn={() => queryClient.invalidateQueries({ queryKey: ['me'] })} />;
  }

  if (me.isError || !me.data) {
    return <p className="error" style={{ padding: 32 }}>The control plane is unreachable.</p>;
  }

  // Every account has exactly one member today, so there is no switcher and no
  // members page. `default_account_id` is what the SPA follows; when a second
  // account appears, this is where a switcher goes and nothing else changes.
  const account = me.data.accounts.find((a) => a.id === me.data?.default_account_id);
  if (!account) {
    return <p className="error" style={{ padding: 32 }}>This login has no account.</p>;
  }

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">xinfer</div>
        <nav className="nav">
          {PAGES.map((name) => (
            <button
              key={name}
              onClick={() => setPage(name)}
              aria-current={page === name ? 'page' : undefined}
            >
              {name}
            </button>
          ))}
        </nav>
        <div style={{ marginTop: 24, padding: '0 10px' }}>
          <div className="muted" style={{ fontSize: 13, wordBreak: 'break-all' }}>
            {me.data.user.name ?? me.data.user.email ?? me.data.user.user_id}
          </div>
          <button
            className="ghost"
            style={{ marginTop: 10, width: '100%' }}
            onClick={() => logout.mutate()}
          >
            Sign out
          </button>
        </div>
      </aside>

      <main className="main">
        {page === 'Overview' && <Overview account={account} />}
        {page === 'Keys' && <Keys account={account} />}
        {page === 'Usage' && <Usage account={account} />}
        {page === 'Limits' && <Limits account={account} />}
        {page === 'Quickstart' && <Quickstart account={account} />}
      </main>
    </div>
  );
}

/**
 * Development sign-in.
 *
 * Production replaces this with a redirect to GitHub or Google; there are no
 * passwords to collect here and there never will be.
 */
function SignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const [email, setEmail] = useState('');
  const signIn = useMutation({
    mutationFn: (value: string) => client.devLogin(value),
    onSuccess: onSignedIn,
  });

  return (
    <div className="login">
      <h1>Sign in to xinfer</h1>
      <p className="subtitle">Development sign-in. Production uses GitHub or Google.</p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (email) signIn.mutate(email);
        }}
      >
        <input
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-label="Email"
        />
        <button
          className="primary"
          type="submit"
          style={{ marginTop: 12, width: '100%' }}
          disabled={!email || signIn.isPending}
        >
          {signIn.isPending ? 'Signing in…' : 'Continue'}
        </button>
      </form>
      {signIn.isError && (
        <p className="error">
          Sign-in failed. Is the api running with <code>--dev-login</code>?
        </p>
      )}
    </div>
  );
}
